import React, {useCallback, useEffect, useRef, useMemo} from 'react';
import {Map} from 'immutable';
import type {QuestionModelV3} from '@albert-io/models';
import {useShallow} from 'zustand/react/shallow';
import {callTargetedAction} from 'client/framework';

import PassageCorrectionPreSubmissionOrDennisRenderer from '../PassageCorrectionPreSubmissionOrDennisRenderer.react';
import PassageCorrectionPostSubmissionRenderer from '../PassageCorrectionPostSubmissionRenderer.react';
import passageCorrectionQuestionActions from '../PassageCorrectionQuestion.actions';
import {
  useQuestionTranslationStore,
  NoTranslationWarningHeader,
  btnDisabledWarning,
  translatedInstructions
} from '../../shared/TranslationSection';

interface PassageCorrectionInputAreaProps {
  storeName: string;
  question: QuestionModelV3;
  isGuessSubmitted: boolean;
  activeQuestionStore?: any;
}

const PassageCorrectionInputArea = ({
  isGuessSubmitted,
  ...props
}: PassageCorrectionInputAreaProps) => {
  const initialCorrections = useRef(Map());

  const store = props.question.getStore?.() || props.activeQuestionStore;

  const {showTranslations, translationInfo} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question,
      translationInfo: state.translatedQuestionInfo
    }))
  );

  useEffect(() => {
    if (!store.hasInitialCorrections()) {
      callTargetedAction({
        name: passageCorrectionQuestionActions.SET_INITIAL_CORRECTIONS,
        payload: initialCorrections.current,
        targetStore: store.getName()
      });
    }
  }, [props.question, store]);

  const addWordToInitialCorrection = useCallback(
    ({text, matchedRegionIndex, optionIndex}) => {
      if (matchedRegionIndex === undefined || store.hasInitialCorrections()) {
        return;
      }
      initialCorrections.current = initialCorrections.current.setIn(
        [matchedRegionIndex, optionIndex],
        text
      );
    },
    [store]
  );

  const translatedText = useMemo(() => {
    return [translationInfo?.uncorrected_text];
  }, [translationInfo]);

  return (
    <div className='passage-correction-question'>
      {showTranslations && (
        <NoTranslationWarningHeader className='mb-x4' text={btnDisabledWarning} />
      )}
      <h2 className='question__prompt-heading'>
        {showTranslations && translatedInstructions.passageCorrection}
        {!showTranslations &&
          (!isGuessSubmitted
            ? 'Click the word(s) you want to edit in the sentence below and type your changes.'
            : 'Click highlighted areas for more information.')}
      </h2>
      {!isGuessSubmitted ? (
        <PassageCorrectionPreSubmissionOrDennisRenderer
          {...props}
          onRenderWordFunc={addWordToInitialCorrection}
          isGuessSubmitted={isGuessSubmitted}
          showTranslation={showTranslations}
          translatedText={translatedText}
          store={store}
        />
      ) : (
        <PassageCorrectionPostSubmissionRenderer
          {...props}
          onRenderWordFunc={addWordToInitialCorrection}
          isGuessSubmitted={isGuessSubmitted}
          showTranslation={showTranslations}
          translatedText={translatedText}
        />
      )}
    </div>
  );
};

export default PassageCorrectionInputArea;
